import { PageHeader, PageTitle } from '@components'
import BalanceAnalyticsChart from '@dashboard/components/BalanceAnalyticsChart'
import KpiCards from '@dashboard/components/KpiCards'
import MethodsByVolumeCard from '@dashboard/components/MethodsByVolumeCard'
import QuickActionsBar from '@dashboard/components/QuickActionsBar'
import TopCountriesCard from '@dashboard/components/TopCountriesCard'
import cn from '@integrations/classnames'
import { type ReactNode, useEffect } from '@integrations/react'
import { useGetAggregatedBalanceQuery, useGetCheckoutAnalyticsMutation } from 'mmfintech-backend-api'
import { isValidArray, tr } from 'mmfintech-commons'
import styles from './EnterpriseDashboard.module.scss'

interface Props {
  /** Dismissible promo cards, laid out as the page's closing row. */
  announcements?: ReactNode
}

function EnterpriseDashboard({ announcements }: Props) {
  const [fetchAnalytics, { data: analytics, isLoading: analyticsLoading }] = useGetCheckoutAnalyticsMutation()

  useEffect(() => {
    fetchAnalytics({ includePreviousPeriod: true })
  }, [fetchAnalytics])

  const { balance } = useGetAggregatedBalanceQuery(
    { dateFrom: '', dateTo: '' },
    {
      selectFromResult: ({ data }) => ({
        balance: isValidArray(data) ? data[data.length - 1] : null
      }),
      refetchOnMountOrArgChange: true
    }
  )

  const topCountries = analytics?.currentPeriod?.topCountries ?? []
  const methodsByVolume = analytics?.currentPeriod?.methodsByVolume ?? []

  return (
    <>
      <PageHeader className={cn('enterprise-dashboard__header')}>
        <PageTitle className={cn('enterprise-dashboard__title')}>
          {tr('FRONTEND.DASHBOARD.HEADING', 'Overview')}
        </PageTitle>
        <QuickActionsBar />
      </PageHeader>

      <div className={cn(styles['enterprise-dashboard'], 'enterprise-dashboard')}>
        <KpiCards analytics={analytics} isLoading={analyticsLoading} />

        <div className={cn(styles['enterprise-dashboard__main-row'], 'enterprise-dashboard__main-row')}>
          <BalanceAnalyticsChart balance={balance} />
          <div className={cn(styles['enterprise-dashboard__side'], 'enterprise-dashboard__side')}>
            <TopCountriesCard countries={topCountries} isLoading={analyticsLoading} />
            <MethodsByVolumeCard methods={methodsByVolume} isLoading={analyticsLoading} />
          </div>
        </div>

        {announcements}
      </div>
    </>
  )
}

export default EnterpriseDashboard
